import { useState } from 'react';
import { Check, Copy, EyeOff } from 'lucide-react';
import { ClaudeCodeGlyph } from '../connectors/BrandGlyphs';
import { Button, Card, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useConnectClaude } from '../../../lib/agent-connections';
import { buildClaudeSkill } from '../../../lib/claude-skill';
import styles from './ConnectClaudeCard.module.css';

const CONNECTIONS_ROUTE = 'settings-connections';

/**
 * "Connect Claude" — mint an act-as-me token for the signed-in user and render the copy-and-paste skill
 * block that carries it. The user copies the block and pastes it into their own Claude Code; no install
 * and no second auth step, because the token is baked in.
 *
 * The token is returned exactly once (it is stored only hashed), so once generated it stays on screen
 * for this visit only — the copy makes that plain rather than pretending it can be re-fetched.
 *
 * Open, the second action is **Hide**, which collapses the block away (Monday 12792391464); the card then
 * offers "Generate instructions" again, and taking it mints a FRESH token. There is deliberately no
 * "re-open the one I just had": dropping the block drops the only copy of that token, so the honest
 * affordance is to mint a new one rather than a Show that would have nothing to show.
 */
export function ConnectClaudeCard() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const connect = useConnectClaude();
  const [skill, setSkill] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const onGenerate = () => {
    logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: 'generate-claude-instructions' });
    connect.mutate(undefined, {
      onSuccess: ({ token }) => {
        setSkill(buildClaudeSkill(token));
        setCopied(false);
      },
    });
  };

  const onHide = () => {
    logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: 'hide-claude-instructions' });
    setSkill(null);
    setCopied(false);
  };

  const onCopy = async () => {
    if (!skill) return;
    logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: 'copy-claude-instructions' });
    try {
      await navigator.clipboard.writeText(skill);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (permissions, insecure context) — the block is on screen and selectable,
      // so the user can still copy by hand; nothing to recover.
    }
  };

  return (
    <Card variant="sunken" className={styles.card}>
      <div className={styles.head}>
        <span className={styles.icon} aria-hidden>
          <ClaudeCodeGlyph size={18} />
        </span>
        <Typography variant="h4">{t('connections.claude.title')}</Typography>
      </div>
      <div className={styles.divider} aria-hidden />
      <Typography variant="p2" color="secondary" className={styles.instructions}>
        {t('connections.claude.subtitle')}
      </Typography>

      {skill ? (
        <>
          <pre className={styles.skill}>{skill}</pre>
          <div className={styles.skillActions}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
              onClick={onCopy}
            >
              {copied ? t('connections.claude.copied') : t('connections.claude.copy')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={<EyeOff size={16} aria-hidden />}
              onClick={onHide}
            >
              {t('connections.connect.hide')}
            </Button>
          </div>
          <Typography variant="p3" color="secondary" className={styles.note}>
            {t('connections.claude.tokenNote')}
          </Typography>
        </>
      ) : (
        <>
          <div className={styles.skillActions}>
            <Button variant="outlined" size="large" fullWidth loading={connect.isLoading} onClick={onGenerate}>
              {t('connections.claude.generate')}
            </Button>
          </div>
          {connect.isError ? (
            <Typography variant="p3" color="secondary" className={styles.note}>
              {t('connections.claude.error')}
            </Typography>
          ) : null}
        </>
      )}
    </Card>
  );
}
